/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/** @jsx jsx */
/** @jsxRuntime classic */
import { jsx, css } from '@emotion/react';
import { useMemo } from 'react';
import Typography from '../Typography/Typography';
import { TypographyVariant } from '../style/typography';
import useTheme from '../style/useTheme';
import InteractiveDiv from '../InteractiveDiv/InteractiveDiv';

export type CheckboxV2Props = React.PropsWithChildren<{
  id: string;
  'aria-label'?: string;
  required?: boolean;
  focused?: boolean;
  checked: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
  typography?: TypographyVariant;
  label: string;
  hovered?: boolean;
}>;

export const BaseCheckboxV2 = (props: CheckboxV2Props): JSX.Element => {
  const {
    id,
    focused,
    hovered,
    checked,
    disabled,
    required,
    'aria-label': ariaLabel,
    onChange,
    typography,
    label,
  } = props;
  const { resolveColor, theme } = useTheme();

  const checkboxFill = useMemo(() => {
    if (checked) {
      return resolveColor({
        type: 'component',
        value: theme.components.checkbox.default.selectedColor,
      });
    }
    return 'transparent';
  }, [checked, resolveColor, theme.components]);

  const checkboxBorder = useMemo(() => {
    if (checked) {
      // selected
      return resolveColor({
        type: 'component',
        value: theme.components.checkbox.default.selectedColor,
      });
    }
    if (focused) {
      // focused
      return resolveColor({
        type: 'theme',
        colorLegacy: 'Primary/50',
        colorNext: 'brand/accent',
      });
    }
    if (hovered) {
      // hover
      return resolveColor({
        type: 'theme',
        colorLegacy: 'Neutral/70',
        colorNext: 'neutral/default',
      });
    }
    if (disabled) {
      // opacity 30%
      // disabled
      return resolveColor({
        type: 'theme',
        colorLegacy: 'Neutral/50',
        colorNext: 'neutral/default',
      });
    }
    if (checked && disabled) {
      // selected disabled
      return resolveColor({
        type: 'theme',
        colorLegacy: 'Primary/50',
        colorNext: 'brand/default',
      });
    }
    // default
    return resolveColor({
      type: 'theme',
      colorLegacy: 'Neutral/50',
      colorNext: 'neutral/default',
    });
  }, [checked, disabled, focused, hovered, resolveColor, theme.components]);

  const labelId = `${id}-Label`;

  return (
    <div style={{ display: 'flex', flexDirection: 'row' }}>
      <InteractiveDiv
        role="checkbox"
        aria-checked={checked}
        aria-required={required}
        aria-disabled={disabled}
        aria-label={ariaLabel}
        aria-labelledby={labelId}
        id={id}
        data-testid={id}
        css={css`
          cursor: pointer;
          box-sizing: border-box;
          width: 22px;
          height: 22px;
          background-color: ${checkboxFill};
          border: ${focused ? '3px' : '2px'} solid ${checkboxBorder};
          display: inline-block;
          position: relative;
          border-radius: 4px;

          // Need this so checkbox does not get squeezed when a parent element is narrow
          flex: none;

          &:after {
            content: '';
            position: absolute;
            width: 5px;
            height: 10px;
            border: solid
              ${resolveColor({
                type: 'component',
                value: theme.components.checkbox.default.selectedCheckColor,
              })};
            border-width: 0 2.5px 2.5px 0;
            top: 2px;
            left: 5px;
            transform: rotate(45deg) scale(0.725);
            display: ${checked ? 'block' : 'none'};
          }
        `}
        onClick={() => {
          if (onChange) onChange(!checked);
        }}
      />
      <div id={labelId} style={{ marginLeft: '8px' }}>
        <Typography variant={typography || 'body'}>{label}</Typography>
      </div>
    </div>
  );
};

export default BaseCheckboxV2;
